import type { LookupItem, SearchResponse } from '@jmc/shared';
import { FILE_STATUSES, formatCount } from '@jmc/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, apiDownload, ApiRequestError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import {
  cx,
  EmptyState,
  ErrorState,
  Field,
  InlineError,
  Modal,
  Mono,
  PageHeader,
  Panel,
  Select,
  Spinner,
  StatusBadge,
  TableSkeleton,
  TextInput,
  Toast,
} from '../components/ui.js';
import {
  LocationPicker,
  LocationStatus,
  type LocationValue,
} from '../components/LocationPicker.js';

/** Waits for typing to pause before searching, so each keystroke is not a query. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Mirrors CSV_ROW_CAP on the server. */
const CSV_ROW_CAP = 20_000;

const FILTER_KEYS = [
  'status',
  'kind',
  'zoneId',
  'departmentId',
  'wardNo',
  'yearFrom',
  'yearTo',
  'roomCode',
  'rackCode',
  // bagId was missing here, so every "what is on this shelf" link from the
  // Almirahs and Record rooms screens silently dropped its filter and showed
  // the clerk all 20,000 files instead. The API supported it the whole time.
  'bagId',
] as const;

export function SearchPage() {
  const { t, pick } = useI18n();
  const { can } = useAuth();
  const canRelocate = can('file:relocate');
  const [params, setParams] = useSearchParams();

  const [term, setTerm] = useState(params.get('q') ?? '');
  const [showFilters, setShowFilters] = useState(() =>
    FILTER_KEYS.some((key) => params.get(key)),
  );
  const debouncedTerm = useDebounced(term);
  const queryClient = useQueryClient();

  // Bulk relocation. A clerk works from a result list -- "these thirty came
  // back from Revenue and now live in R-14" -- so the selection lives here
  // rather than on the shelf map.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // The URL is the source of truth, so a search can be bookmarked or sent to
  // a colleague and reopened exactly as it was.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedTerm) next.set('q', debouncedTerm);
    else next.delete('q');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTerm]);

  const lookups = useQuery({
    queryKey: ['lookups'],
    queryFn: () => api<{ departments: LookupItem[]; zones: LookupItem[] }>('/files/lookups'),
    staleTime: 10 * 60_000,
  });

  const queryString = useMemo(() => {
    const search = new URLSearchParams();
    if (debouncedTerm) search.set('q', debouncedTerm);
    for (const key of FILTER_KEYS) {
      const value = params.get(key);
      if (value) search.set(key, value);
    }
    search.set('limit', '25');
    return search;
  }, [debouncedTerm, params]);

  // A selection belongs to the result list it was made in. Changing the
  // search or a filter leaves the clerk with rows that are no longer on
  // screen still armed for a move.
  useEffect(() => {
    setSelected(new Set());
  }, [queryString]);

  const results = useInfiniteQuery({
    queryKey: ['files', queryString.toString()],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const search = new URLSearchParams(queryString);
      if (pageParam) search.set('cursor', pageParam);
      return api<SearchResponse>(`/files?${search.toString()}`);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const pages = results.data?.pages ?? [];
  const items = pages.flatMap((page) => page.items);
  const first = pages[0];

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const clearFilters = () => {
    const next = new URLSearchParams();
    if (debouncedTerm) next.set('q', debouncedTerm);
    setParams(next, { replace: true });
  };

  const activeFilters = FILTER_KEYS.filter((key) => params.get(key)).length;

  const toggle = (id: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allOnScreenSelected = items.length > 0 && items.every((file) => selected.has(file.id));

  return (
    <>
      <PageHeader
        title={t('nav.search')}
        actions={
          <>
            {/* Carries the current filters straight through, so "print what I
                am looking at" needs no second search. */}
            <Link to={`/pick-list?${params.toString()}`} className="btn-secondary">
              {t('action.pickList')}
            </Link>
            {/* The same filters, as a spreadsheet, for the lists that leave
                the building: audit requests, section handovers. */}
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                const total = first?.totalCount ?? 0;
                if (total > CSV_ROW_CAP && !window.confirm(t('export.tooMany', { cap: CSV_ROW_CAP, total }))) {
                  return;
                }
                void apiDownload(
                  `/files/export.csv?${queryString.toString()}`,
                  `JMC-files-${new Date().toISOString().slice(0, 10)}.csv`,
                );
              }}
            >
              {t('action.exportCsv')}
            </button>
            {can('file:create') && (
              <Link to="/files/new" className="btn-primary">
                {t('file.new')}
              </Link>
            )}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-0 flex-1">
          <svg
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="8.75" cy="8.75" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path d="m12.5 12.5 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <TextInput
            type="search"
            value={term}
            autoFocus
            onChange={(event) => setTerm(event.target.value)}
            placeholder={t('search.placeholder')}
            aria-label={t('search.placeholder')}
            className="pl-9"
          />
        </div>
        <button
          type="button"
          className={cx('btn-secondary', activeFilters > 0 && 'border-navy-300 text-navy-700')}
          onClick={() => setShowFilters((open) => !open)}
          aria-expanded={showFilters}
        >
          {t('search.filters')}
          {activeFilters > 0 && (
            <span className="rounded-full bg-navy-700 px-1.5 text-[0.6875rem] font-semibold text-white">
              {activeFilters}
            </span>
          )}
        </button>
      </div>

      {showFilters && (
        <Panel className="mb-4" bodyClassName="grid gap-3 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t('field.status')}>
            <Select
              value={params.get('status') ?? ''}
              onChange={(event) => setFilter('status', event.target.value)}
            >
              <option value="">{t('common.all')}</option>
              {FILE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`status.${status}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('field.zone')}>
            <Select
              value={params.get('zoneId') ?? ''}
              onChange={(event) => setFilter('zoneId', event.target.value)}
            >
              <option value="">{t('common.all')}</option>
              {lookups.data?.zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {pick(zone.nameEn, zone.nameHi)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('field.department')}>
            <Select
              value={params.get('departmentId') ?? ''}
              onChange={(event) => setFilter('departmentId', event.target.value)}
            >
              <option value="">{t('common.all')}</option>
              {lookups.data?.departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {pick(department.nameEn, department.nameHi)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('field.ward')}>
            <TextInput
              inputMode="numeric"
              value={params.get('wardNo') ?? ''}
              onChange={(event) => setFilter('wardNo', event.target.value)}
              placeholder={t('common.all')}
            />
          </Field>

          <Field label={`${t('field.year')} — ${t('action.previous')}`}>
            <TextInput
              inputMode="numeric"
              placeholder="1985"
              value={params.get('yearFrom') ?? ''}
              onChange={(event) => setFilter('yearFrom', event.target.value)}
            />
          </Field>

          <Field label={`${t('field.year')} — ${t('action.next')}`}>
            <TextInput
              inputMode="numeric"
              placeholder="2026"
              value={params.get('yearTo') ?? ''}
              onChange={(event) => setFilter('yearTo', event.target.value)}
            />
          </Field>

          <Field label={t('field.room')}>
            <TextInput
              value={params.get('roomCode') ?? ''}
              onChange={(event) => setFilter('roomCode', event.target.value)}
              placeholder={t('common.all')}
            />
          </Field>

          <div className="flex items-end">
            <button type="button" className="btn-ghost" onClick={clearFilters} disabled={activeFilters === 0}>
              {t('action.clear')}
            </button>
          </div>
        </Panel>
      )}

      {first && (
        <div className="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.8125rem] text-ink-500">
          <span>
            <span className="font-medium text-ink-700 tabular">
              {formatCount(first.totalCount ?? items.length)}
            </span>{' '}
            {t('search.results')}
          </span>
          <span aria-hidden="true">·</span>
          <span>{t(`search.strategy.${first.strategy}`)}</span>
          <span aria-hidden="true">·</span>
          <span className="tabular">
            {t('search.took')} {first.tookMs} ms
          </span>
        </div>
      )}

      <Panel bodyClassName="overflow-x-auto">
        {results.isLoading && <TableSkeleton columns={6} />}
        {results.isError && (
          <div className="p-4">
            <ErrorState error={results.error} onRetry={() => void results.refetch()} />
          </div>
        )}

        {results.isSuccess && items.length === 0 && (
          <EmptyState
            title={t('search.noResults')}
            hint={debouncedTerm ? t('search.noResultsHint') : t('search.hint')}
          />
        )}

        {/*
          Phones get stacked cards, not a sideways-scrolling table. A clerk at
          the counter needs the status and the shelf location at a glance; in a
          table at 375px both sit off the right edge.
        */}
        {items.length > 0 && (
          <ul className="divide-y divide-line md:hidden">
            {items.map((file) => (
              <li key={file.id}>
                <Link to={`/files/${file.id}`} className="block px-4 py-3 hover:bg-navy-50/60">
                  <div className="flex items-start justify-between gap-3">
                    <Mono className="font-medium text-navy-600">{file.eFileNumber}</Mono>
                    <StatusBadge status={file.status} overdue={file.isOverdue} />
                  </div>
                  <p className="mt-1 font-medium">
                    {pick(file.firstAllotteeEn, file.firstAllotteeHi) ||
                      pick(file.subjectEn, file.subjectHi) ||
                      t(`kind.${file.kind}`)}
                  </p>
                  <p className="text-[0.8125rem] text-ink-500">
                    {[pick(file.schemeNameEn, file.schemeNameHi), file.plotNo]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {file.location && (
                    <p className="mt-1.5 text-[0.8125rem] text-ink-700">
                      <span className="text-ink-500">{t('field.location')}: </span>
                      <Mono>
                        {file.location.roomCode} / {file.location.rackCode} /{' '}
                        {file.location.columnCode} / {file.location.bagCode}
                      </Mono>
                    </p>
                  )}
                  {file.status === 'ISSUED' && file.issuedTo && (
                    <p className="text-[0.8125rem] text-ink-500">
                      {t('field.issuedTo')}: {file.issuedTo}
                    </p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}

        {items.length > 0 && (
          <table className="data-table hidden md:table">
            <thead>
              <tr>
                {canRelocate && (
                  <th className="w-8">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-line-strong"
                      aria-label={t('action.selectAll')}
                      checked={allOnScreenSelected}
                      onChange={(event) =>
                        setSelected(
                          event.target.checked
                            ? new Set(items.map((file) => file.id))
                            : new Set(),
                        )
                      }
                    />
                  </th>
                )}
                <th>{t('field.eFileNumber')}</th>
                <th>{t('field.allottee')} / {t('field.subject')}</th>
                <th className="hidden md:table-cell">{t('field.location')}</th>
                <th className="hidden lg:table-cell">{t('field.ward')}</th>
                <th className="hidden lg:table-cell">{t('field.year')}</th>
                <th>{t('field.status')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((file) => (
                <tr key={file.id} className={cx(selected.has(file.id) && 'bg-navy-50/60')}>
                  {canRelocate && (
                    <td>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-line-strong"
                        aria-label={`${t('action.select')} ${file.eFileNumber}`}
                        checked={selected.has(file.id)}
                        onChange={() => toggle(file.id)}
                      />
                    </td>
                  )}
                  <td className="whitespace-nowrap">
                    <Link
                      to={`/files/${file.id}`}
                      className="font-medium text-navy-600 hover:underline"
                    >
                      <Mono>{file.eFileNumber}</Mono>
                    </Link>
                    {file.fileNumber && (
                      <div className="mt-0.5 max-w-[16rem] truncate text-[0.75rem] text-ink-400">
                        {file.fileNumber}
                      </div>
                    )}
                  </td>
                  <td className="max-w-[22rem]">
                    <div className="truncate font-medium">
                      {pick(file.firstAllotteeEn, file.firstAllotteeHi) ||
                        pick(file.subjectEn, file.subjectHi) ||
                        '—'}
                    </div>
                    <div className="truncate text-[0.8125rem] text-ink-500">
                      {[pick(file.schemeNameEn, file.schemeNameHi), file.plotNo]
                        .filter(Boolean)
                        .join(' · ') || t(`kind.${file.kind}`)}
                    </div>
                  </td>
                  <td className="hidden whitespace-nowrap md:table-cell">
                    {file.location ? (
                      <Mono className="text-ink-700">
                        {file.location.roomCode} / {file.location.rackCode} /{' '}
                        {file.location.columnCode} / {file.location.bagCode}
                      </Mono>
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                  <td className="hidden lg:table-cell">{file.wardNo ?? '—'}</td>
                  <td className="hidden whitespace-nowrap lg:table-cell">
                    {file.financialYear ?? '—'}
                  </td>
                  <td>
                    <StatusBadge status={file.status} overdue={file.isOverdue} />
                    {file.status === 'ISSUED' && file.issuedTo && (
                      <div className="mt-0.5 max-w-[10rem] truncate text-[0.75rem] text-ink-500">
                        {file.issuedTo}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {results.hasNextPage && (
          <div className="border-t border-line p-3 text-center">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void results.fetchNextPage()}
              disabled={results.isFetchingNextPage}
            >
              {results.isFetchingNextPage && <Spinner />}
              {t('action.loadMore')}
            </button>
          </div>
        )}
      </Panel>

      {/* A bar rather than a toolbar above the table: it only exists while
          something is selected, and it says what will happen to how many. */}
      {canRelocate && selected.size > 0 && (
        <div className="sticky bottom-3 z-10 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-[6px] border border-navy-300 bg-navy-700 px-4 py-3 text-white shadow-lg">
          <span className="text-[0.875rem]">
            {t('bulk.selected', { n: selected.size })}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-[5px] px-3 py-1.5 text-[0.8125rem] font-medium text-white/80 hover:text-white"
              onClick={() => setSelected(new Set())}
            >
              {t('action.clear')}
            </button>
            <button
              type="button"
              className="rounded-[5px] bg-white px-3 py-1.5 text-[0.8125rem] font-semibold text-navy-700 hover:bg-paper"
              onClick={() => setMoveOpen(true)}
            >
              {t('bulk.moveTo')}
            </button>
          </div>
        </div>
      )}

      {moveOpen && (
        <BulkMoveDialog
          fileIds={[...selected]}
          onClose={() => setMoveOpen(false)}
          onDone={(moved) => {
            setMoveOpen(false);
            setSelected(new Set());
            setToast(t('bulk.moved', { n: moved }));
            void queryClient.invalidateQueries({ queryKey: ['files'] });
            void queryClient.invalidateQueries({ queryKey: ['location-codes'] });
          }}
        />
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}

      {items.length > 0 && (
        <p className="mt-3 hidden text-[0.8125rem] text-ink-400 md:block">
          {t('field.location')}: {t('field.room')} / {t('field.rack')} / {t('field.column')} /{' '}
          {t('field.bag')}
        </p>
      )}
    </>
  );
}

/**
 * Move every selected file onto one shelf.
 *
 * Deliberately the same four pickers as the entry form, so the destination is
 * chosen from what exists rather than typed from memory, and a shelf that
 * does not exist yet is called out before the move rather than created in
 * silence.
 */
function BulkMoveDialog({
  fileIds,
  onClose,
  onDone,
}: {
  fileIds: number[];
  onClose: () => void;
  onDone: (moved: number) => void;
}) {
  const { t } = useI18n();
  const [to, setTo] = useState<LocationValue>({
    roomCode: '',
    rackCode: '',
    columnCode: '',
    bagCode: '',
  });
  const [error, setError] = useState<string | null>(null);

  const complete =
    to.roomCode.trim() && to.rackCode.trim() && to.columnCode.trim() && to.bagCode.trim();

  const move = useMutation({
    mutationFn: () =>
      api<{ moved: number }>('/locations/relocate-files', {
        method: 'POST',
        body: {
          fileIds,
          to: {
            roomCode: to.roomCode.trim(),
            rackCode: to.rackCode.trim(),
            columnCode: to.columnCode.trim(),
            bagCode: to.bagCode.trim(),
          },
        },
      }),
    onSuccess: (result) => onDone(result.moved),
    onError: (caught) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('common.error')),
  });

  return (
    <Modal
      open
      title={t('bulk.moveTitle', { n: fileIds.length })}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('action.cancel')}
          </button>
          <button
            type="submit"
            form="bulk-move"
            className="btn-primary"
            disabled={move.isPending || !complete}
          >
            {move.isPending && <Spinner />}
            {t('bulk.moveTo')}
          </button>
        </>
      }
    >
      {error && <div className="mb-4"><InlineError message={error} /></div>}

      <form
        id="bulk-move"
        className="space-y-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setError(null);
          move.mutate();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <LocationPicker value={to} onChange={setTo} disabled={move.isPending} />
        </div>
        <LocationStatus value={to} />
      </form>
    </Modal>
  );
}
